<script lang="ts">
	// Svelte hooks
	import { get } from 'svelte/store';
	import { createEventDispatcher } from 'svelte';
	//Formating
	import { format } from 'svelte-i18n';
	// Helpers
	import { devices } from 'src/stores/lists/devices';
	import { real_id } from 'src/helpers/interface_helpers';
	// Constants
	import { SLEEPING_TYPES } from '../../constants';
	import { getEventTableHeadings, eventLogHasRecordingExpand } from './event_log_helper';
	import {
		ExpandableTable,
		ExpandableTableCol,
		ExpandableTableRow,
		Icon,
		Text,
		LabelList,
		LabelListV2,
	} from '@Shelly/shelly-design-system';
	import type { TextPropsTypes } from '@Shelly/shelly-design-system';
	//Components
	import IconForInputReader from './IconForInputReader.svelte';
	import CameraEventExpanded from './CameraEventExpanded.svelte';
	import { timestampToDate } from 'src/helpers/date_helper';
	import { setSidePanelState } from '../Sidepanel/sidepanel-helpers';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';

	const dispatch = createEventDispatcher();

	export let events = [], // get event log data in UI format
		devicePageLog = false,
		deviceType = '';

	export let type: 'global' | 'group' | 'device' = 'global';
	export let openedEventTimestamp = null;

	const EVENT_TABLE_HEADINGS = getEventTableHeadings();

	const COL_WIDTHS_BY_TYPE = {
		global: ['36px', '1fr', '1fr', '1fr', '36px'],
		group: ['1fr', '1fr', '1fr', '36px'],
		device: ['1fr', '36px'],
	};
	const COL_WIDTHS_MOBILE_BY_TYPE = {
		global: ['22px', '1fr', '1fr', '1fr', '22px'],
		group: ['1fr', '1fr', '1fr', '36px'],
		device: ['1fr', '36px'],
	};

	const HEADING_BY_TYPE = {
		global: [...Object.values(EVENT_TABLE_HEADINGS)],
		group: [
			EVENT_TABLE_HEADINGS.timestamp,
			EVENT_TABLE_HEADINGS.room,
			EVENT_TABLE_HEADINGS.name,
			EVENT_TABLE_HEADINGS.info,
		],
		device: [EVENT_TABLE_HEADINGS.timestamp, EVENT_TABLE_HEADINGS.info],
	};

	const deviceColWidths = ['1fr', '36px'];

	// const colWidths = devicePageLog
	// 	? deviceColWidths
	// 	: ['36px', '1fr', '1fr', '1fr', '36px'];

	const colWidths = COL_WIDTHS_BY_TYPE[type];

	// const colWidthsMobile = devicePageLog
	// 	? deviceColWidths
	// 	: ['22px', '1fr', '1fr', '1fr', '22px'];

	const colWidthsMobile = COL_WIDTHS_MOBILE_BY_TYPE[type];

	const isSpecialDevice =
		type === 'device' //devicePageLog
			? SLEEPING_TYPES.find((t) => t === deviceType)
			: false;

	let isMobile: boolean;
	let expandAll = false;

	let headingElements = HEADING_BY_TYPE[type]; //[...Object.values(EVENT_TABLE_HEADINGS)];

	// if (devicePageLog) {
	// 	headingElements = [
	// 		EVENT_TABLE_HEADINGS.timestamp,
	// 		EVENT_TABLE_HEADINGS.info
	// 	];
	// }

	$: expandedRows = events.map((e) => {
		const labelListData = Object.values(e.data).filter((k: any) => {
			if (k.hidden) return false;
			return ![
				'S',
				'SS',
				'SSS',
				'SL',
				'LS',
				'L',
				'LL',
				'status_update',
				'unknown',
				'undefined',
				'Locked',
				'Unlocked',
				'open',
			].includes(k.value);
		});

		if (/_100$/.test(e.id)) {
			labelListData.push({
				name: 'addon',
				value: $format('settings_title_addon_sensors', {
					default: 'Add-on peripherals',
				}),
				icon: null,
			});
		}

		return {
			eventTimestamp: e.timestamp,
			heading: false,
			expanded: false,
			notExpandable: labelListData.length === 0 && !eventLogHasRecordingExpand(e),
			labelListData: labelListData,
		};
	});

	$: isAllRowsNotExpandable =
		type === 'device' && expandedRows.findIndex((el) => !el.notExpandable) === -1;

	$: styleCondition = isMobile && !devicePageLog;

	$: if (openedEventTimestamp) {
		closeAllExpanded();

		const indexOfOpenedEvent = expandedRows?.findIndex(
			(el) => el.eventTimestamp === openedEventTimestamp,
		);

		if (indexOfOpenedEvent > -1) expandedRows[indexOfOpenedEvent].expanded = true;

		openedEventTimestamp = null;
	}

	/**Event object format => {timestamp: Date.now(), room: "", name: "" ,
	 * eventID: 0, data : [], units: undefined, type : "" }*/
	const _prepareElements = (event) => {
		let elements: any = [
			...(type !== 'global' ? [] : [{ icon: event.catIcon, style: '' }]),
			timestampToDate(event.timestamp),
		];
		if (type !== 'device') {
			elements = [...elements, event.room, event.name];
		}
		elements.push(event.icon);

		return elements;
	};

	const closeAllExpanded = () => {
		expandedRows = expandedRows.map((event) => {
			return {
				...event,
				expanded: false,
			};
		});
	};

	const handleHeadingClick = (e, index) => {
		if (headingElements[index] && headingElements[index].sort) {
			headingElements[index].desc = !headingElements[index].desc;
			let title = headingElements[index].id; //get id because of sort is on english id

			dispatch('sortBy', { title, desc: headingElements[index].desc });
		}
	};

	const checkExpandAllManual = () => {
		const isAllExpanded = expandedRows.every((row) => {
			if (!row.notExpandable && !row.heading) {
				return row.expanded === true;
			} else {
				return true;
			}
		});
		expandAll = isAllExpanded;
	};

	const onExpand = (i: number) => {
		expandedRows = expandedRows.map((row, index) => {
			if (index === i) {
				return {
					...row,
					expanded: !row.expanded,
				};
			} else {
				return row;
			}
		});

		checkExpandAllManual();
	};

	const onExpandAll = () => {
		expandAll = !expandAll;
		expandedRows = expandedRows.map((row) => {
			return {
				...row,
				expanded: expandAll && !row.notExpandable,
			};
		});
	};

	const getTextType = (
		styleCondition: boolean,
		isTableHeading = false,
	): TextPropsTypes['styleType'] => {
		if (isTableHeading) {
			return styleCondition ? 'body-small' : 'h2';
		}
		return styleCondition ? 'tiny' : 'body-small';
	};

	const getIconSize = (styleCondition: boolean) => {
		return styleCondition ? 'xs' : 's';
	};

	const handleClick = (event) => {
		if (event.category === 'scene' || event.category === 'alarm') {
			setSidePanelState({ type: event.category, id: event.sceneId });
		} else {
			const allDevices = get(devices);
			const device = allDevices[event.id] || allDevices[real_id(event.id)];
			if (device?.id) {
				setSidePanelState({ type: 'device', id: device.id });
			}
		}
		setSidePanelState({ open: true });
	};

	const addBackgroundColorToLabels = (labels: any[]) => {
		return labels.map((label: any) => ({
			...label,
			backgroundColor: $showNewLayout ? '--color-base' : '--color-1',
		}));
	};

	if (import.meta.env.DEV) {
		console.log('%cEvents', 'color:lightblue; font-size:14px;', events);
	}
</script>

<ExpandableTable
	backgroundColor={$showNewLayout ? 'color-0' : undefined}
	rowsColor={$showNewLayout && type !== 'global' ? 'color-0' : 'color-base'}
	bind:isMobile
	colWidths={isMobile ? colWidthsMobile : colWidths}
	class="mb-m"
	isSimple={type === 'group' || isAllRowsNotExpandable}
>
	<!-- isSimple={type === 'group' || (type === 'device' && !isSpecialDevice)} if necessary bring back that condition to ExpandableTable but i think it is not needed. Also this will disable collapse if device isn't sleeping type device -->
	<!-- Table Heading -->
	<ExpandableTableRow
		on:expand={onExpandAll}
		heading={true}
		expanded={expandAll}
		notExpandable={isAllRowsNotExpandable}
	>
		{#each headingElements as col, i}
			<ExpandableTableCol>
				{#if col.icon}
					<Icon class="width-full" icon={col.icon} size={getIconSize(styleCondition)} />
				{/if}
				{#if col.title && !col.icon}
					<Text
						styleType={getTextType(styleCondition, true)}
						breakWord
						class="text-capitalize text-bold"
						on:click={(e) => handleHeadingClick(e, i)}
					>
						{col.title}
					</Text>
				{/if}
			</ExpandableTableCol>
		{/each}
	</ExpandableTableRow>
	<!-- Table Heading -->

	<!-- Table Body -->
	{#each events as event, i (event)}
		<ExpandableTableRow
			on:expand={() => onExpand(i)}
			heading={false}
			expanded={expandedRows[i].expanded}
			notExpandable={expandedRows[i].notExpandable}
			rowClickExpand
		>
			{#each _prepareElements(event) as col, i}
				<ExpandableTableCol>
					{#if col.icon}
						{#if typeof col.icon !== 'string'}
							<!-- added object type for col.icon because the need to use custom icons -->
							<IconForInputReader
								class="width-full"
								shrinkOnMobile
								event={col.icon.value}
							/>
						{:else}
							<Icon
								class="width-full"
								icon={col.icon}
								size={getIconSize(styleCondition)}
								style={col.style}
							/>
						{/if}
					{/if}
					{#if col && !col.icon && col.icon !== ''}
						<Text
							class={i === 3 && 'link-styles text-small '}
							styleType={getTextType(styleCondition)}
							breakWord
							on:click={(e) => {
								if (i === 3) {
									e.stopPropagation();
									handleClick(event);
								}
							}}
						>
							{col}
						</Text>
					{/if}
				</ExpandableTableCol>
			{/each}

			<svelte:fragment slot="expandableContent">
				{#if eventLogHasRecordingExpand(event)}
					<CameraEventExpanded {event} {isMobile} />
				{:else if $showNewLayout}
					<LabelListV2
						a11yProps={{
							isInteractive: false,
						}}
						labelList={expandedRows[i].labelListData}
						size={isMobile ? 's' : 'm'}
						backgroundColor="--color-1"
					/>
				{:else}
					<LabelList
						slot="expandableContent"
						labelList={expandedRows[i].labelListData}
						size={isMobile ? 's' : 'm'}
					/>
				{/if}
			</svelte:fragment>
		</ExpandableTableRow>
	{/each}
	<!-- Table Body -->
</ExpandableTable>
